import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import api from '../utils/api';
import './Courses.css';

const FALLBACK_IMG = '/assets/hero/hero-bg.webp';

function CourseCard({ course }) {
  const isOpen = course.access_mode === 'open';
  const isEnquire = course.pricing_mode === 'enquire';
  const isFree = course.pricing_mode === 'free';
  const price = Number(course.cost_inr) || 0;

  // Enrollment status (per-student, only present when signed in). Drives
  // both the corner chip on the image AND the foot CTA copy.
  const enrolStatus = course.enrollment_status; // null | pending | paid | self_enrolled | cancelled
  const hasAccess  = enrolStatus === 'paid' || enrolStatus === 'self_enrolled';
  const isPending  = enrolStatus === 'pending';

  let chipLabel, chipModifier;
  if (hasAccess) {
    chipLabel = 'Enrolled';
    chipModifier = 'enrolled';
  } else if (isPending) {
    chipLabel = 'Payment pending';
    chipModifier = 'pending';
  } else if (isOpen) {
    chipLabel = 'Open access';
    chipModifier = 'open';
  } else {
    chipLabel = 'Enrol to access';
    chipModifier = 'gated';
  }

  let priceLabel;
  if (hasAccess) priceLabel = '✓ Enrolled';
  else if (isPending) priceLabel = 'Awaiting confirmation';
  else if (isEnquire) priceLabel = 'Enquire';
  else if (isFree || isOpen) priceLabel = 'Free';
  else priceLabel = `₹${price.toLocaleString('en-IN')}`;

  const ctaLabel = hasAccess ? 'Continue learning' : isPending ? 'View status' : 'View course';

  return (
    <Link to={`/courses/${course.slug}`} className="course-card">
      <div className="course-card__image">
        <img src={course.image || FALLBACK_IMG} alt={course.title} loading="lazy" />
        <span className={`course-card__access course-card__access--${chipModifier}`}>
          {chipLabel}
        </span>
      </div>
      <div className="course-card__body">
        <h3 className="course-card__title">{course.title}</h3>
        {course.subtitle && <p className="course-card__subtitle">{course.subtitle}</p>}
        <div className="course-card__foot">
          <span className={`course-card__price ${(isFree || isOpen || hasAccess) ? 'course-card__price--free' : ''}`}>
            {priceLabel}
          </span>
          <span className="course-card__cta">
            {ctaLabel}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </Link>
  );
}

function CoursesPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.getCourses()
      .then((data) => { if (active) setCourses(Array.isArray(data) ? data : []); })
      .catch(() => { /* show empty state */ })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  return (
    <>
      <PageBanner title="Courses" />
      <section className="courses-list">
        <div className="container">
          {loading ? (
            <div className="courses-list__empty">Loading courses…</div>
          ) : courses.length === 0 ? (
            <div className="courses-list__empty">No courses available yet — check back soon.</div>
          ) : (
            <div className="courses-list__grid">
              {courses.map((c) => <CourseCard key={c.id} course={c} />)}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default CoursesPage;
